import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Download, Info, Search, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIEWS = { pfs: 'PFS SHARE', all: 'ALL PARTIES' };
const ROLE_LABEL = { merchant: 'Merchant', iso: 'ISO / Broker', syndicator: 'Syndicator' };
const NA = 'not available';

// null = not available (never a fabricated $0.00); an explicit dash ("—") is passed through
const money = (v, dash) => {
  if (dash) return dash;
  if (v === null || v === undefined) return NA;
  const n = Number(v);
  const s = `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < -0.004 ? `-${s}` : s;
};
const pct = (v, d = 2) => (v === null || v === undefined ? '' : `${(Number(v) * 100).toFixed(d)}%`);
const ratio = (r) => (!r ? NA : r.display || pct(r.value));
const tone = (v) => (v === null || v === undefined ? 'text-gray-400' : Number(v) < -0.004 ? 'text-red-700' : 'text-gray-900');

const Money = ({ v, dash, className = '' }) => (
  <span className={`tabular-nums ${tone(dash ? 0 : v)} ${className}`}>{money(v, dash)}</span>
);

const Help = ({ text }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="inline h-3.5 w-3.5 text-gray-400 ml-1 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-sm whitespace-pre-line">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/* ─── search ─────────────────────────────────────────────────────────────── */
const PartySearch = ({ onPick }) => {
  const { getAuthHeaders } = useAuth();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const box = useRef(null);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return undefined; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await axios.get(`${API}/reports/client/search`, { headers: getAuthHeaders(), params: { q } });
        setResults(res.data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, getAuthHeaders]);

  return (
    <div ref={box} className="relative max-w-xl">
      <Label className="text-xs">Search a merchant, ISO/broker or syndicator</Label>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          className="pl-8"
          placeholder="Company name, DBA, shortcode or contact name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          data-testid="client-report-search"
        />
        {q && (
          <button type="button" className="absolute right-2.5 top-2.5 text-gray-400" onClick={() => { setQ(''); setResults([]); }}>
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto">
          {busy && <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>}
          {!busy && results.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">No matching party</div>}
          {results.map((r) => (
            <div key={r.id} className="px-3 py-2 border-b last:border-b-0">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{r.company_name}{r.dba ? <span className="text-gray-500"> · DBA {r.dba}</span> : null}</div>
                  <div className="text-xs text-gray-500">{r.shortcode}{r.name ? ` · ${r.name}` : ''}</div>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {r.roles.length === 0 && <span className="text-xs text-gray-400">no deals / positions</span>}
                  {r.roles.map((role) => (
                    <Button key={role} size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => { onPick(r, role); setOpen(false); setQ(''); }}>
                      {ROLE_LABEL[role]} ({r.counts[role]})
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── sections ───────────────────────────────────────────────────────────── */
const Stat = ({ label, children, help }) => (
  <Card className="bg-white">
    <CardContent className="p-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}{help && <Help text={help} />}</div>
      <div className="text-lg font-bold mt-0.5">{children}</div>
    </CardContent>
  </Card>
);

const DealsTable = ({ deals, view, showMerchant, footnotes }) => (
  <Card>
    <CardHeader className="pb-2"><CardTitle className="text-base">Deals ({deals.length})</CardTitle></CardHeader>
    <CardContent className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Deal #</TableHead>
            {showMerchant && <TableHead>Merchant</TableHead>}
            <TableHead>Funded</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Funding</TableHead>
            <TableHead className="text-right">Factor</TableHead>
            <TableHead className="text-right">Payback</TableHead>
            <TableHead className="text-right">Collected</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
            <TableHead className="text-right">Synd %</TableHead>
            <TableHead className="text-right">PFS share</TableHead>
            <TableHead className="text-right">Commission</TableHead>
            <TableHead className="text-right">P&amp;L</TableHead>
            <TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deals.map((d) => {
            const v = d.views[view];
            return (
              <TableRow key={d.deal_uuid} className={d.class === 'cancelled' ? 'opacity-50' : ''}>
                <TableCell className="font-medium">{d.deal_number}{d.lettered && <sup title={footnotes.lettered}>†</sup>}</TableCell>
                {showMerchant && <TableCell>{d.merchant}</TableCell>}
                <TableCell>{formatDate(d.funded_date)}</TableCell>
                <TableCell>
                  {d.status}
                  {d.ongoing && <Badge variant="outline" className="ml-1 text-[10px] border-amber-400 text-amber-700">ONGOING</Badge>}
                  {d.written_off && <Badge variant="outline" className="ml-1 text-[10px] border-red-400 text-red-700">WRITTEN OFF</Badge>}
                  {d.accounting_review && <Badge variant="outline" className="ml-1 text-[10px]">NEEDS REVIEW</Badge>}
                </TableCell>
                <TableCell className="text-right"><Money v={v.gross_funded} /></TableCell>
                <TableCell className="text-right tabular-nums">{d.factor ?? ''}</TableCell>
                <TableCell className="text-right"><Money v={v.payback} /></TableCell>
                <TableCell className="text-right"><Money v={v.collected} /></TableCell>
                <TableCell className="text-right">
                  {d.credit ? <span className="text-emerald-700 tabular-nums">credit {money(d.credit)}</span> : <Money v={v.outstanding} />}
                </TableCell>
                <TableCell className="text-right tabular-nums">{d.synd_pct}%</TableCell>
                <TableCell className="text-right tabular-nums">{pct(d.pfs_share, 1)}</TableCell>
                <TableCell className="text-right"><Money v={v.commission} dash={d.commission_display} /></TableCell>
                <TableCell className="text-right"><Money v={v.pnl} /></TableCell>
                <TableCell className="text-xs text-gray-600 max-w-[16rem]">{d.notes.join('; ')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const PnlTable = ({ summary }) => {
  const cols = [['realized', 'REALIZED'], ['open', 'OPEN (as of today)'], ['total', 'TOTAL']];
  const lines = [
    ['Capital deployed', 'capital_deployed'], ['Collections received', 'collections'], ['Commission paid', 'commission'],
    ['Lawyer fees / legal costs', 'lawyer_costs'], ['NET REVENUE', 'net_revenue'], ['P&L', 'pnl'],
    ['Write-offs (visibility only)', 'write_offs'], ['Fees charged (information only)', 'fees_charged'],
  ];
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Profit &amp; loss</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow><TableHead /> {cols.map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {lines.map(([label, key]) => (
              <TableRow key={key} className={key === 'net_revenue' || key === 'pnl' ? 'font-semibold bg-slate-50' : ''}>
                <TableCell>{label}</TableCell>
                {cols.map(([k]) => (
                  <TableCell key={k} className="text-right">
                    {summary.pnl[k] ? <Money v={summary.pnl[k][key]} /> : <span className="text-gray-300">—</span>}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell>Fees kept<Help text={summary.fees_kept.why} /></TableCell>
              <TableCell colSpan={3} className="text-right text-gray-500">{summary.fees_kept.display}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-xs text-gray-500 mt-2">
          Realized = terminal deals (Paid in Full, Settled, Refinanced, written off). Open = as they stand today; Default stays
          open until settled, paid or written off. An empty column means no deals in that bucket.
        </p>
      </CardContent>
    </Card>
  );
};

const CapitalRoi = ({ summary, formulas }) => {
  const { capital: c, roi: r } = summary;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <Stat label="Gross funded"><Money v={c.gross_funded} /></Stat>
      <Stat label="Net new capital" help={formulas.net_new_capital}><Money v={c.net_new_capital} /><div className="text-xs text-gray-500 font-normal">refi payoffs {money(c.refi_payoffs)}</div></Stat>
      <Stat label="Capital deployed" help={c.roi_denominator}><Money v={c.capital_deployed} /></Stat>
      <Stat label="ROI realized" help={formulas.roi_realized}>{ratio(r.roi_realized)}</Stat>
      <Stat label="Annualized ROI" help={`${formulas.weighted_days}\n${formulas.annualized_roi}`}>
        {ratio(r.annualized_roi)}
        <div className="text-xs text-gray-500 font-normal">weighted days {r.weighted_days ?? NA}{r.weighted_days_excluded_deals ? ` (${r.weighted_days_excluded_deals} undated excluded)` : ''}</div>
      </Stat>
      <Stat label="ROI incl. open" help={formulas.roi_incl_open}>{ratio(r.roi_incl_open)}</Stat>
      <Stat label="Projected P&L at full collection" help={formulas.projected_at_full}>
        <Money v={r.projected_at_full.value} /><div className="text-xs text-amber-700 font-normal">projection — not in realized figures</div>
      </Stat>
    </div>
  );
};

const IsoTable = ({ rows, footnotes }) => (
  <Card>
    <CardHeader className="pb-2"><CardTitle className="text-base">ISO / Broker</CardTitle></CardHeader>
    <CardContent className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>ISO</TableHead><TableHead>Deal #</TableHead><TableHead className="text-right">Funding</TableHead><TableHead className="text-right">Points</TableHead><TableHead className="text-right">Commission total</TableHead><TableHead className="text-right">Syndicator share</TableHead><TableHead className="text-right">PFS cost</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-gray-500">No broker on these deals</TableCell></TableRow>}
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.iso}{r.co_brokered && <Badge variant="outline" className="ml-1 text-[10px]">co-brokered</Badge>}</TableCell>
              <TableCell>{r.deal_number}{r.footnotes.length > 0 && <sup title={footnotes.lettered_no_commission}>†</sup>}</TableCell>
              <TableCell className="text-right"><Money v={r.funding} /></TableCell>
              <TableCell className="text-right tabular-nums">{r.points ?? ''}</TableCell>
              <TableCell className="text-right"><Money v={r.commission_total} dash={r.display} /></TableCell>
              <TableCell className="text-right"><Money v={r.syndicator_share} dash={r.display} /></TableCell>
              <TableCell className="text-right"><Money v={r.pfs_cost} dash={r.display} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const SyndicatorRows = ({ rows, footnotes }) => (
  <Card>
    <CardHeader className="pb-2"><CardTitle className="text-base">Syndicators <span className="text-xs font-normal text-gray-500">— the same figures they see on the investor portal</span></CardTitle></CardHeader>
    <CardContent className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Syndicator</TableHead><TableHead>Deal #</TableHead><TableHead className="text-right">Synd %</TableHead><TableHead className="text-right">Capital wired</TableHead><TableHead className="text-right">Commission wired</TableHead><TableHead className="text-right">Total in</TableHead><TableHead className="text-right">Collections</TableHead><TableHead className="text-right">Returned</TableHead><TableHead>Cap basis</TableHead><TableHead className="text-right">Cap remaining</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={11} className="text-gray-500">No syndication on these deals</TableCell></TableRow>}
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.syndicator}</TableCell>
              <TableCell>{r.deal_number}</TableCell>
              <TableCell className="text-right tabular-nums">{r.ownership_percentage}%</TableCell>
              <TableCell className="text-right"><Money v={r.funding_contribution} /></TableCell>
              <TableCell className="text-right"><Money v={r.broker_commission} /></TableCell>
              <TableCell className="text-right"><Money v={r.total_contribution} /></TableCell>
              <TableCell className="text-right"><Money v={r.collections} /></TableCell>
              <TableCell className="text-right"><Money v={r.returned_to_date} /></TableCell>
              <TableCell>{r.cap_basis || NA}{r.cap_basis === 'settlement' && <Help text={footnotes.cap_settlement} />}</TableCell>
              <TableCell className="text-right"><Money v={r.cap_remaining} /></TableCell>
              <TableCell>{r.status}{r.accounting_review && <Badge variant="outline" className="ml-1 text-[10px]">NEEDS REVIEW</Badge>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const MerchantBody = ({ rep, view }) => {
  const s = rep.summary[view];
  const id = rep.identity;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat label="Legal name / DBA"><span className="text-sm">{id.company_name}{id.dba ? ` · ${id.dba}` : ''}</span></Stat>
        <Stat label="Shortcode"><span className="text-sm">{id.shortcode || '—'}</span></Stat>
        <Stat label="Contact"><span className="text-sm">{id.contact || '—'}</span></Stat>
        <Stat label="Deals">{id.deal_count}</Stat>
        <Stat label="First funded"><span className="text-sm">{formatDate(id.first_funded) || '—'}</span></Stat>
        <Stat label="Open">{id.open_count}</Stat>
      </div>
      <DealsTable deals={rep.deals} view={view} footnotes={rep.header.footnotes} />
      <CapitalRoi summary={s} formulas={rep.header.formulas} />
      <PnlTable summary={s} />
      <IsoTable rows={rep.counterparties.iso} footnotes={rep.header.footnotes} />
      <SyndicatorRows rows={rep.counterparties.syndicators} footnotes={rep.header.footnotes} />
    </div>
  );
};

const IsoBody = ({ rep, view }) => {
  const s = rep.summary[view];
  const id = rep.identity;
  const p = rep.performance;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat label="ISO / Broker"><span className="text-sm">{id.company_name}</span></Stat>
        <Stat label="Deals sourced">{id.deals_sourced}</Stat>
        <Stat label="First"><span className="text-sm">{formatDate(id.first_funded) || '—'}</span></Stat>
        <Stat label="Last"><span className="text-sm">{formatDate(id.last_funded) || '—'}</span></Stat>
        <Stat label="Active">{id.active_count}</Stat>
        <Stat label="Co-brokered">{p.co_brokered_deals}</Stat>
      </div>
      <DealsTable deals={rep.deals} view={view} showMerchant footnotes={rep.header.footnotes} />
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Performance of their paper <span className="text-xs font-normal text-gray-500">(all parties)</span></CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat label="Total funded through them"><Money v={p.total_funded} /></Stat>
          <Stat label="Total commission paid"><Money v={p.total_commission} /></Stat>
          <Stat label="Collected vs payback"><Money v={p.collected} /><div className="text-xs text-gray-500 font-normal">of {money(p.payback)}</div></Stat>
          <Stat label="Default rate">{p.default_count} of {id.deals_sourced} ({pct(p.default_rate_count, 1)})<div className="text-xs text-gray-500 font-normal">{money(p.default_funded)} funded · {pct(p.default_rate_dollars, 1)} of $</div></Stat>
          <Stat label="Write-offs on their deals"><Money v={p.write_offs} /></Stat>
          <Stat label="Avg days to default">{p.avg_days_to_default ?? NA}{p.days_to_default_missing ? <div className="text-xs text-gray-500 font-normal">{p.days_to_default_missing} without a default date</div> : null}</Stat>
        </CardContent>
      </Card>
      <CapitalRoi summary={s} formulas={rep.header.formulas} />
      <PnlTable summary={s} />
      <Card className="border-red-200">
        <CardHeader className="pb-2"><CardTitle className="text-base text-red-800">Money-losing paper <span className="text-xs font-normal text-gray-500">— commission paid on deals that defaulted with less collected than funded</span></CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Deal #</TableHead><TableHead>Merchant</TableHead><TableHead className="text-right">Funding</TableHead><TableHead className="text-right">Collected</TableHead><TableHead className="text-right">Commission paid</TableHead></TableRow></TableHeader>
            <TableBody>
              {rep.money_losing.length === 0 && <TableRow><TableCell colSpan={5} className="text-gray-500">None</TableCell></TableRow>}
              {rep.money_losing.map((r) => (
                <TableRow key={r.deal_number}><TableCell>{r.deal_number}</TableCell><TableCell>{r.merchant}</TableCell><TableCell className="text-right"><Money v={r.funding} /></TableCell><TableCell className="text-right"><Money v={r.collected} /></TableCell><TableCell className="text-right"><Money v={r.commission_paid} dash={r.commission_display} /></TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const SyndicatorBody = ({ rep, view }) => {
  const m = rep.money;
  const fn = rep.header.footnotes;
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">Source: {rep.source}. Profit and ROI are {m.profit_label}.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={m.total_in_label ? `Total in — ${m.total_in_label}` : 'Total in'}><Money v={m.total_in} /></Stat>
        <Stat label="Collections"><Money v={m.collections} /></Stat>
        <Stat label="Total returned"><Money v={m.total_returned} /></Stat>
        <Stat label={`Profit (${m.profit_label})`}><Money v={m.profit} /></Stat>
        <Stat label="Their ROI">{ratio(m.roi)}</Stat>
        <Stat label="Available balance"><Money v={m.available_balance} /></Stat>
        <Stat label={`Reserved — pending withdrawals (${m.reserved_requests})`}><Money v={m.reserved} /></Stat>
        <Stat label={`Legal statements issued, unpaid (${m.legal_statements_unpaid_count})`} help={m.legal_statements_unpaid_note}><Money v={m.legal_statements_unpaid} /></Stat>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Positions ({rep.positions.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Deal #</TableHead><TableHead>Merchant</TableHead><TableHead className="text-right">Synd %</TableHead><TableHead className="text-right">Capital wired</TableHead><TableHead className="text-right">Commission wired</TableHead><TableHead className="text-right">Total in</TableHead><TableHead className="text-right">Collections</TableHead><TableHead className="text-right">Returned</TableHead><TableHead className="text-right">Available</TableHead><TableHead>Cap basis</TableHead><TableHead className="text-right">Cap amount</TableHead><TableHead className="text-right">Cap remaining</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {rep.positions.map((p) => (
                <TableRow key={p.deal_uuid}>
                  <TableCell>{p.deal_number}</TableCell><TableCell>{p.merchant}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.synd_pct}%</TableCell>
                  <TableCell className="text-right"><Money v={p.capital_wired} /></TableCell>
                  <TableCell className="text-right"><Money v={p.commission_wired} /></TableCell>
                  <TableCell className="text-right"><Money v={p.total_in} /></TableCell>
                  <TableCell className="text-right"><Money v={p.collections_to_date} /></TableCell>
                  <TableCell className="text-right"><Money v={p.returned_to_date} /></TableCell>
                  <TableCell className="text-right"><Money v={p.available_balance} /></TableCell>
                  <TableCell>{p.cap_basis || NA}{p.cap_basis === 'settlement' && <Help text={fn.cap_settlement} />}</TableCell>
                  <TableCell className="text-right"><Money v={p.cap_amount} /></TableCell>
                  <TableCell className="text-right"><Money v={p.cap_remaining} /></TableCell>
                  <TableCell>{p.status}{p.accounting_review && <Badge variant="outline" className="ml-1 text-[10px]">NEEDS REVIEW</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Legal statement history</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Period</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead>Bank reference</TableHead></TableRow></TableHeader>
            <TableBody>
              {rep.statement_history.length === 0 && <TableRow><TableCell colSpan={5} className="text-gray-500">No legal statements</TableCell></TableRow>}
              {rep.statement_history.map((st) => (
                <TableRow key={st.reference}><TableCell>{st.reference}</TableCell><TableCell>{st.period}</TableCell><TableCell className="text-right"><Money v={st.total} /></TableCell><TableCell>{st.status}</TableCell><TableCell>{st.bank_reference || ''}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">PFS alongside them — {VIEWS[view]}</h3>
        <PnlTable summary={rep.pfs_side[view]} />
      </div>
    </div>
  );
};

/* ─── shell ──────────────────────────────────────────────────────────────── */
const ClientReport = () => {
  const { getAuthHeaders } = useAuth();
  const [party, setParty] = useState(null);           // {id, company_name, roles}
  const [role, setRole] = useState(null);
  const [view, setView] = useState('pfs');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const params = useMemo(() => ({ role, from: from || undefined, to: to || undefined }), [role, from, to]);

  const load = useCallback(async () => {
    if (!party || !role) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/reports/client/${party.id}`, { headers: getAuthHeaders(), params });
      setRep(res.data);
    } catch (e) {
      setRep(null);
      setError(e?.response?.data?.detail || 'Failed to load the report');
    } finally {
      setLoading(false);
    }
  }, [party, role, params, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (ext) => {
    try {
      const res = await axios.get(`${API}/reports/client/${party.id}/export.${ext}`, {
        headers: getAuthHeaders(), params: { ...params, view }, responseType: 'blob',
      });
      const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `client-report.${ext}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(`Failed to export ${ext.toUpperCase()}`);
    }
  };

  const pick = (r, roleToOpen) => { setParty(r); setRole(roleToOpen); setRep(null); };

  return (
    <div className="space-y-4" data-testid="client-report">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <PartySearch onPick={pick} />
          {party && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="mr-auto">
                <div className="text-lg font-semibold">{party.company_name}</div>
                <div className="text-xs text-gray-500">Read-only report · header: Power Funding Solutions</div>
              </div>
              {party.roles.length > 1 && (
                <Tabs value={role} onValueChange={(r) => { setRole(r); setRep(null); }}>
                  <TabsList>{party.roles.map((r) => <TabsTrigger key={r} value={r}>{ROLE_LABEL[r]}</TabsTrigger>)}</TabsList>
                </Tabs>
              )}
              {role !== 'syndicator' && (
                <>
                  <div><Label className="text-xs">Collections from</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
                  <div><Label className="text-xs">to</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
                  {(from || to) && <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); }}>Lifetime</Button>}
                </>
              )}
              <div>
                <Label className="text-xs">View</Label>
                <Tabs value={view} onValueChange={setView}>
                  <TabsList>
                    <TabsTrigger value="pfs" data-testid="client-report-view-pfs">PFS SHARE</TabsTrigger>
                    <TabsTrigger value="all" data-testid="client-report-view-all">ALL PARTIES</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <Button size="sm" variant="outline" disabled={!rep} onClick={() => exportFile('csv')}><Download className="h-3 w-3 mr-1" />CSV</Button>
              <Button size="sm" variant="outline" disabled={!rep} onClick={() => exportFile('pdf')}><Download className="h-3 w-3 mr-1" />PDF</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {!party && <p className="text-sm text-gray-500">Search a name to see that party's full picture. Ambiguous names show a pick-list; a party with several roles gets one tab per role.</p>}
      {error && <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3">{error}</div>}
      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      {rep && !loading && (
        <TooltipProvider>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge className={view === 'pfs' ? 'bg-slate-800' : 'bg-orange-700'}>{VIEWS[view]}</Badge>
            <span className="text-gray-600">
              {view === 'pfs' ? "PFS's own money: full deal economics × PFS's retained share (1 − syndicated %)." : 'Full deal economics, all parties combined.'}
            </span>
            <Badge variant="outline">{rep.header.period.label}</Badge>
            <span className="text-gray-400">as of {formatDate(rep.header.as_of)}</span>
          </div>
          {role === 'merchant' && <MerchantBody rep={rep} view={view} />}
          {role === 'iso' && <IsoBody rep={rep} view={view} />}
          {role === 'syndicator' && <SyndicatorBody rep={rep} view={view} />}
          <div className="text-xs text-gray-500 space-y-0.5 pt-2 border-t">
            {Object.entries(rep.header.footnotes).map(([k, t]) => <div key={k}>{k === 'lettered' ? '† ' : ''}{t}</div>)}
            <div>No ledger or deal data is changed by this report.</div>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
};

export default ClientReport;
